import { useCallback, useMemo } from "react"

import { differenceInCalendarDays } from "date-fns"

import { useShiftExceptions } from "@/hooks/useCollections"
import { buildCoverIndex, buildCoveredIndex } from "@/lib/shiftCover"
import type { PlannedRoute, ShiftException } from "@/types/collections"

export interface NotCheckedReason {
  label: string
  why: string
}

/** Shift cover per route instance, worst flag wins; a row with no instance shows nothing. */
export function usePlanningShiftCover({
  from,
  to,
  rotaHorizonDays,
}: {
  from: string | undefined
  to: string | undefined
  rotaHorizonDays: number
}) {
  const {
    data: shiftExceptions,
    isLoading,
    error: shiftExceptionsError,
  } = useShiftExceptions({ from, to }, !!from && !!to)

  const coverByInstance = useMemo(
    () => buildCoverIndex(shiftExceptions?.exceptions),
    [shiftExceptions],
  )
  const coveredByInstance = useMemo(
    () => buildCoveredIndex(shiftExceptions?.covered),
    [shiftExceptions],
  )

  const coverFor = useCallback(
    (route: PlannedRoute): ShiftException[] | undefined =>
      route.routeInstanceId ? coverByInstance.get(route.routeInstanceId) : undefined,
    [coverByInstance],
  )
  const coveredFor = useCallback(
    (route: PlannedRoute) =>
      route.routeInstanceId ? coveredByInstance.get(route.routeInstanceId) : undefined,
    [coveredByInstance],
  )

  // Same scope rules as supabase/functions/_shared/shiftExceptions.ts.
  const notCheckedReason = useCallback(
    (route: PlannedRoute): NotCheckedReason | null => {
      if (shiftExceptionsError) {
        return {
          label: "Check unavailable",
          why: `Couldn't read the shift comparison: ${(shiftExceptionsError as Error).message}`,
        }
      }
      if (!route.routeInstanceId) {
        return { label: "No instance yet", why: "Cover is checked once the route instance exists" }
      }
      if (route.routeInstanceStatus === "cancelled") {
        return { label: "Cancelled", why: "A cancelled route has nothing to staff" }
      }
      if (route.routeInstanceStarted) {
        return { label: "Started", why: "The route has gone out; cover is no longer reported" }
      }
      const daysAhead = differenceInCalendarDays(route.date, new Date())
      if (daysAhead > rotaHorizonDays) {
        return {
          label: "Not checked yet",
          why: `Shifts are booked about ${rotaHorizonDays} days ahead, so cover is checked from then`,
        }
      }
      return null
    },
    [shiftExceptionsError, rotaHorizonDays],
  )

  return { isLoading, coverFor, coveredFor, notCheckedReason }
}
